import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Truck, BellRinging, Plus, CalendarBlank } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";

const DEST = { zincare: "Zincare", vopsire: "Vopsire", santier: "Șantier", beneficiar: "Beneficiar final" };
const CARRIER = { propriu: "Propriu (Parc Auto)", extern: "Extern" };
const STATUS = { planificat: "Planificat", in_curs: "În curs", livrat: "Livrat" };
const STATUS_COLOR = { planificat: "text-amber-300", in_curs: "text-sky-300", livrat: "text-emerald-300" };

export default function TransportPlanner() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selDate, setSelDate] = useState(null);
  const [f, setF] = useState({ destination_type: "zincare", carrier_type: "propriu", carrier_name: "", scheduled_date: "", project_id: "", project_code: "", notes: "" });

  const load = () => api.get("/transports").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const onProject = (pid) => {
    const p = projects.find((x) => x.id === pid);
    setF({ ...f, project_id: pid, project_code: p?.code || "" });
  };

  const add = async () => {
    try { await api.post("/transports", f); setF({ ...f, carrier_name: "", scheduled_date: "", project_id: "", project_code: "", notes: "" }); load(); toast.success("Transport adăugat"); }
    catch (e) { toast.error("Eroare la adăugare"); }
  };
  const setStatus = async (id, status) => { await api.patch(`/transports/${id}`, { status }); load(); };
  const notify = async (id) => {
    try { const r = await api.post(`/transports/${id}/notify-montaj`); toast.success(`Montaj anunțat (${r.data.notified} persoane)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const fmt = (d) => {
    if (!d) return "";
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const scheduledDates = useMemo(
    () => rows.filter((t) => t.scheduled_date).map((t) => new Date(`${t.scheduled_date}T00:00:00`)),
    [rows]
  );
  const selKey = selDate ? fmt(selDate) : "";
  const filtered = useMemo(
    () => (selKey ? rows.filter((t) => t.scheduled_date === selKey) : rows),
    [rows, selKey]
  );

  return (
    <div className="mt-6" data-testid="transport-planner">
      <h2 className="flex items-center gap-2 text-sm font-semibold"><Truck size={18} weight="bold" /> Planificator transporturi</h2>
      <p className="text-xs text-muted-foreground">Transport propriu (Parc Auto) sau extern, către zincare/vopsire, șantier sau beneficiarul final.</p>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-6 rounded-xl border border-border bg-white/[0.02] p-3">
        <select data-testid="tp-destination" value={f.destination_type} onChange={(e) => setF({ ...f, destination_type: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-2 text-sm">
          {Object.entries(DEST).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="tp-carrier" value={f.carrier_type} onChange={(e) => setF({ ...f, carrier_type: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-2 text-sm">
          {Object.entries(CARRIER).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input data-testid="tp-carrier-name" placeholder="Transportator / mașină" value={f.carrier_name} onChange={(e) => setF({ ...f, carrier_name: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-2 text-sm" />
        <input data-testid="tp-date" type="date" value={f.scheduled_date} onChange={(e) => setF({ ...f, scheduled_date: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-2 text-sm" />
        <select data-testid="tp-project" value={f.project_id} onChange={(e) => onProject(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-2 text-sm">
          <option value="">— Proiect —</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</option>)}
        </select>
        <Button onClick={add} data-testid="tp-add" className="gap-1"><Plus size={16} /> Adaugă</Button>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[auto_1fr]">
        <div className="rounded-xl border border-border bg-white/[0.02] p-2" data-testid="tp-calendar">
          <div className="flex items-center justify-between px-2 pt-1">
            <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground"><CalendarBlank size={14} /> Calendar transporturi</span>
            {selDate && <button onClick={() => setSelDate(null)} data-testid="tp-cal-clear" className="text-xs text-sky-300 hover:underline">Toate</button>}
          </div>
          <Calendar
            mode="single"
            selected={selDate}
            onSelect={setSelDate}
            modifiers={{ scheduled: scheduledDates }}
            modifiersClassNames={{ scheduled: "relative font-semibold text-teal-300 after:absolute after:bottom-1 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-teal-400" }}
          />
          {selDate && <p className="px-2 pb-1 text-center text-xs text-muted-foreground">Ziua selectată: {selKey}</p>}
        </div>

        <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase text-muted-foreground"><tr>
            <th className="px-3 py-2 text-left">Destinație</th><th className="px-3 py-2 text-left">Transportator</th>
            <th className="px-3 py-2 text-left">Dată</th><th className="px-3 py-2 text-left">Proiect</th>
            <th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Acțiuni</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {filtered.map((t) => (
              <tr key={t.id} data-testid={`tp-row-${t.id}`}>
                <td className="px-3 py-2">{DEST[t.destination_type] || t.destination_type}</td>
                <td className="px-3 py-2">{CARRIER[t.carrier_type] || t.carrier_type}{t.carrier_name ? ` · ${t.carrier_name}` : ""}</td>
                <td className="px-3 py-2 text-muted-foreground">{t.scheduled_date || "—"}</td>
                <td className="px-3 py-2">{t.project_code || "—"}</td>
                <td className="px-3 py-2">
                  <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} data-testid={`tp-status-${t.id}`} className={`rounded-md border border-border bg-background px-2 py-1 text-xs ${STATUS_COLOR[t.status] || ""}`}>
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  {t.destination_type === "beneficiar" && (
                    <div className="flex flex-col gap-1">
                      {t.notified_montaj_planned && <span className="text-[11px] text-teal-300" data-testid={`tp-prenotified-${t.id}`}>✓ Anunțat din timp</span>}
                      <Button size="sm" variant={t.notified_montaj ? "secondary" : "default"} onClick={() => notify(t.id)} data-testid={`tp-notify-${t.id}`} className="h-7 gap-1 text-xs">
                        <BellRinging size={14} /> {t.notified_montaj ? "Montaj anunțat (pe drum)" : "Anunță plecarea"}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-sm text-muted-foreground">{selDate ? "Niciun transport în ziua selectată." : "Niciun transport planificat."}</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
